import { UserStatus } from '@bp/bp-graphql-types';
import { t } from 'i18next';
import { getAdminRoleTranslation } from 'utils/adminRoles';
import { getGroupRoleTranslation } from 'utils/groupRoles';
import { getProfileRoleTranslation } from 'utils/profileRoles';
import { naturalStringSort } from 'utils/stringHelper';
import { getUserStatusTranslation } from 'utils/userStatusUtils';
import { PersonLabelSource, PersonLabelType } from '../types/personTypes';

export function getPersonLabelValueTranslation(source: PersonLabelSource, value: string): string {
  switch (source) {
    case 'AccessRole':
      return getAdminRoleTranslation(value);
    case 'Child':
      return t('persons.labels.child');
    case 'Parent':
      return t('persons.labels.parent');
    case 'ProfileRole':
      return getProfileRoleTranslation(value);
    case 'UserStatus':
      return getUserStatusTranslation(value);
    case 'GroupRole':
      return getGroupRoleTranslation(value);
    case 'AddOn':
    case 'Class':
    case 'ClassGroup':
    default:
      return value;
  }
}

export function getPersonLabelSubValueTranslation(source: PersonLabelSource, subValue: string): string {
  switch (source) {
    case 'Class':
      if (subValue === 'TUTOR') {
        return t('classes.tutor', { context: 'short' });
      }
      return subValue;
    case 'AddOn':
      if (subValue === 'INVITED') {
        return t('userStatus.invited');
      }
      return subValue;
    default:
      return subValue;
  }
}

export function getPersonLabelSourceTranslation(source: PersonLabelSource): string {
  switch (source) {
    case 'AccessRole':
      return t('adminRoles.title');
    case 'AddOn':
      return t('persons.labels.addOn');
    case 'Child':
      return t('persons.labels.child');
    case 'Class':
      return t('classes.title', { count: 1 });
    case 'ClassGroup':
      return t('classGroups.title', { count: 1 });
    case 'GroupRole':
      return t('persons.labels.groupRole');
    case 'Parent':
      return t('persons.labels.parent');
    case 'ProfileRole':
      return t('persons.labels.profileRole');
    case 'UserStatus':
      return t('persons.labels.userStatus');
    default:
      return '';
  }
}

function findInSourceUuids(label: PersonLabelType, labels: PersonLabelType[]): boolean {
  return !!labels.find((l) => label.sourceUuids?.some((uuid) => l.sourceUuids?.includes(uuid)));
}

function findInValue(label: PersonLabelType, labels: PersonLabelType[]): boolean {
  return !!labels.find((l) => l.value === label.value);
}

// TODO: for future use?
// function findInSubValue(label: PersonLabelType, labels: PersonLabelType[]): boolean {
//   return !!labels.find((l) => l.subValue === label.subValue);
// }

export function personHasLabel(label: PersonLabelType, selectedLabels: PersonLabelType[]): boolean {
  if (
    label.source === 'AccessRole' ||
    label.source === 'AddOn' ||
    label.source === 'GroupRole' ||
    label.source === 'ProfileRole' ||
    label.source === 'UserStatus'
  ) {
    return findInValue(label, selectedLabels);
  } else if (
    label.source === 'Child' ||
    label.source === 'Class' ||
    label.source === 'ClassGroup' ||
    label.source === 'Parent'
  ) {
    return findInSourceUuids(label, selectedLabels);
  } else {
    return false;
  }
}

export function labelIsInLabels(label: PersonLabelType, labels: PersonLabelType[]): boolean {
  return personHasLabel(label, labels);
}

function getPersonLabelSourceOrder(source: PersonLabelSource): number {
  switch (source) {
    case 'ProfileRole':
      return 1;
    case 'AccessRole':
      return 2;
    case 'GroupRole':
      return 3;
    case 'Class':
      return 4;
    case 'ClassGroup':
      return 5;
    case 'UserStatus':
      return 6;
    case 'AddOn':
      return 7;
    case 'Parent':
      return 8;
    case 'Child':
      return 9;
    default:
      return 10;
  }
}

export function sortPersonLabelSource(a: PersonLabelSource, b: PersonLabelSource): number {
  return getPersonLabelSourceOrder(a) - getPersonLabelSourceOrder(b);
}

export function uniqueByLabelValue(labels: PersonLabelType[]): PersonLabelType[] {
  const seenValues = new Set<string>();
  return labels.filter((l) => {
    if (seenValues.has(l.value) || l.source === 'Parent' || l.source === 'Child') return false;
    seenValues.add(l.value);
    return true;
  });
}

export function uniqueByLabelSourceUuids(labels: PersonLabelType[]): PersonLabelType[] {
  const seenUuids = new Set<string>();
  return labels.filter((l) => {
    const key = l.sourceUuids ? l.sourceUuids.sort().join(',') : '';
    if (seenUuids.has(key)) return false;
    seenUuids.add(key);
    return true;
  });
}

export const sortByValue = (a: PersonLabelType, b: PersonLabelType) => {
  const valueComparison = naturalStringSort(a.value, b.value);
  if (valueComparison !== 0) {
    return valueComparison;
  }
  const subValueA = a.subValue || '';
  const subValueB = b.subValue || '';
  return naturalStringSort(subValueA, subValueB);
};

export const sortByStatusTranslation = (a: PersonLabelType, b: PersonLabelType) => {
  const statusA = getUserStatusTranslation(a.value);
  const statusB = getUserStatusTranslation(b.value);
  return naturalStringSort(statusA, statusB);
};

export const sortByAccessRoleTranslation = (a: PersonLabelType, b: PersonLabelType) => {
  const roleA = getProfileRoleTranslation(a.value);
  const roleB = getProfileRoleTranslation(b.value);
  return naturalStringSort(roleA, roleB);
};

export const sortByAdminRolesTranslation = (a: PersonLabelType, b: PersonLabelType) => {
  const roleA = getAdminRoleTranslation(a.value);
  const roleB = getAdminRoleTranslation(b.value);
  return naturalStringSort(roleA, roleB);
};

export const accountHasPerson = (status: UserStatus | string | undefined) => {
  return status === UserStatus.ImpersonateOnly || status === UserStatus.Full || status === UserStatus.Invited;
};
